/**
 * # Spatial-builder helpers (PR-V2)
 *
 * Bridges the metadata-only `EntityMetadataIndex` (introduced in
 * PR-V1) into the rbush `SpatialIndex` shape (`RenderableEntity[]`),
 * and lifts visible-set ids back into full `RenderableEntity` records
 * via the `VisibleSetCache` + per-id lookup.
 *
 * # Why a separate module
 *
 * Scene3DStage.tsx is already large (>2400 LOC); the cache-direct
 * render path adds ~150 lines of plumbing that has no React /
 * THREE-mesh concern. Extracting the pure data transforms into this
 * file keeps Scene3DStage focused on the lifecycle of GL resources.
 *
 * # Spatial-stub vs full RenderableEntity
 *
 * The PR-V2 spatial index stores "spatial-stub" records — full
 * `RenderableEntity` shape but with a placeholder `geometry` payload
 * because we don't pay the per-entity preview/adapter cost up-front
 * for entities outside the viewport. Consumers that need bbox / id /
 * zOrder / layer (`intersect`, viewport-cull, layer filter) work
 * directly off the stub. Consumers that need analytic geometry
 * (`pickFromCandidates` distance refinement) call
 * `liftRenderableForPicking` to upgrade the small candidate set into
 * full RenderableEntities through the cache.
 *
 * The stub's `geometry` is a degenerate `point` payload at the bbox
 * centre — never rendered (BatchedLayer never sees a stub; only
 * lifted entities feed the renderer) but valid enough that consumers
 * which assume `geometry` is non-null don't crash.
 */

import type { EntityPreview } from "@dxf-ai-editor/contracts";
import { unionBounds } from "@dxf-ai-editor/cad-core";

import type { RenderableEntity } from "../types";
import { boundsScreenSizePx, HYSTERESIS_BANDS } from "../lod";
import type { EntityMetadata, EntityMetadataIndex } from "../../state/entityMetadataIndex";
import type { AdapterContext } from "../adapters";
import {
  renderableFromDrawingEntity,
  typedAnnotativeHidden,
  type TypedEntityResolver,
} from "../adapters/typedFirstRenderable";
import {
  normalizeActiveScale,
  resolveTypedAnnotation,
} from "../adapters/typed/annotativeResolve";
import { textFromEntity } from "../adapters/typed/textFromEntity";
import { mtextFromEntity } from "../adapters/typed/mtextFromEntity";
import { applyOffsetToBounds } from "../coordinateOrigin";
import {
  flattenBlockInsertForRender,
  liftBlockInsertRenderable,
} from "../scene/liftBlockInsert";
import type { VisibleSetCache } from "./visibleSetCache";

/**
 * Build the spatial-stub array for an entire space, ready to feed
 * `SpatialIndex.bulkLoad`.
 *
 * Iterates the metadata index in insertion order — which matches the
 * DRAWORDER order the importer wrote records in — so `zOrder` is set
 * to the iteration index. Consumers (`pickFromCandidates`) re-sort by
 * `(zOrder asc, id asc)` for tie-breaking, so this monotonic
 * assignment is sufficient.
 *
 * Records with non-finite bounds are skipped; the spatial index would
 * reject NaN bbox queries silently and the missing record carries no
 * render fidelity (zero-area entities that lie outside any viewport).
 */
export function buildSpatialRecordsFromMetadata(
  metadataIndex: EntityMetadataIndex,
  spaceId: string,
): RenderableEntity[] {
  return buildSpatialRecordsForSpaces(metadataIndex, [spaceId]);
}

/**
 * ORDERED MULTI-SPACE variant of {@link buildSpatialRecordsFromMetadata} — the
 * one the cache-direct bulk load actually calls.
 *
 * # Why more than one space
 *
 * The render feed a bulk-load pass consumes is NOT one space: on a paper
 * layout it is `[...activeSpaceEntities, ...modelSpaceEntities]`
 * (`scene/SceneBulkLoad.ts`, the legacy branch's `sourceEntities`), because
 * AutoCAD paints model geometry on a sheet through its VIEWPORTs and the
 * per-viewport scissor passes (`rendering/viewportRenderPass.ts`) can only mask
 * objects that are ALREADY in the scene. The cache-direct branch has to build
 * the same union out of the metadata index, in the same order, or a layout
 * renders a blank sheet (no paper stubs) or empty viewports (no model stubs).
 *
 * # Order and DRAWORDER
 *
 * `spaceIds` is consumed in the given order and de-duplicated (first
 * occurrence wins); the records of the first space precede the records of the
 * second, mirroring the legacy feed concatenation. Within one space the
 * metadata index's insertion order — which is the DRAWORDER order the importer
 * wrote records in — is preserved, and `zOrder` is the record's index in the
 * FINAL array, so `pickFromCandidates`'s `(zOrder asc, id asc)` tie-break sees
 * the same total order the legacy feed produces.
 *
 * The index is walked ONCE (bucketed by space) rather than once per space, so
 * a two-space pass over a 685k-entity index costs exactly what the one-space
 * pass costs today.
 *
 * Records with non-finite bounds are skipped, as in the single-space case.
 *
 * # Coordinate frame — the ONE seam
 *
 * The spatial index is ALWAYS in the renderer's OFFSET-world frame. It is a
 * derived representation of the scene graph, and the scene graph is one frame:
 * the THREE camera, every geometry adapter's output (`sceneGeometryBuilders.ts`
 * → `applyOffset`) and therefore every rect / point the renderer hands the
 * index (`scene/FrameLoops.tsx`'s viewport rect, `stageInteractions.ts`'s pick
 * point, every OSNAP mode) are offset-world.
 *
 * `EntityMetadataIndex` stays RAW/AutoCAD-world — it is the document store, not
 * a render derivative (`state/entityMetadataIndex.ts boundsForEntity` reads
 * entity coordinates verbatim). So the conversion belongs at the render
 * boundary, and THIS function is that boundary: it is the only place raw
 * metadata becomes an index record, so every raw `meta.bounds` goes through
 * `applyOffsetToBounds` here and nowhere else.
 *
 * Before this, the cache-direct branch loaded the index with raw stubs while
 * the queries stayed offset-world, so under a non-zero locked origin (a survey
 * / UTM drawing) every query missed by the whole origin — blank canvas, dead
 * pick, dead window-select, dead OSNAP. Every fixture in this repo has origin
 * `0` (`state/editorStoreImportHelpers.ts createCoordinateOriginAccumulator`
 * rounds the content-bbox centre to the nearest 1e6), which is why the suite
 * was green on it; `scene3d/__tests__/coordinateOriginInvariance.metamorphic.test.ts`
 * makes the origin an explicit generated input so it cannot hide again.
 */
export function buildSpatialRecordsForSpaces(
  metadataIndex: EntityMetadataIndex,
  spaceIds: ReadonlyArray<string>,
): RenderableEntity[] {
  const bySpace = new Map<string, EntityMetadata[]>();
  // De-duplicate while preserving first-occurrence order: the Map's own
  // insertion order IS the emit order below.
  for (const spaceId of spaceIds) {
    if (!bySpace.has(spaceId)) bySpace.set(spaceId, []);
  }
  if (bySpace.size === 0) return [];
  for (const meta of metadataIndex.values()) {
    const bucket = bySpace.get(meta.spaceId);
    if (bucket === undefined) continue;
    if (!isFiniteBounds(meta.bounds)) continue;
    bucket.push(meta);
  }
  const records: RenderableEntity[] = [];
  let zOrder = 0;
  for (const bucket of bySpace.values()) {
    for (const meta of bucket) {
      // THE ONE SEAM raw metadata becomes an index record (see this
      // function's "Coordinate frame" doc above). `meta.bounds` is the
      // document frame; the spatial index is the RENDER frame.
      records.push(
        metadataToSpatialStub(
          { ...meta, bounds: applyOffsetToBounds(meta.bounds) },
          zOrder,
        ),
      );
      zOrder += 1;
    }
  }
  return records;
}

/**
 * Convert a single `EntityMetadata` into the spatial-stub shape the
 * rbush index stores. Exported for unit tests.
 *
 * FRAME-AGNOSTIC BY CONTRACT: this function copies whatever bounds it is
 * handed and never converts. Its two callers supply the render
 * (offset-world) frame by different routes — `buildSpatialRecordsForSpaces`
 * runs the raw metadata box through `applyOffsetToBounds` first (the one
 * seam), while `createMetadataStubResolver` passes `getBounds(id)`, which is
 * read back out of the index and therefore already offset-world. Converting
 * here would double-apply the origin on that second path.
 *
 * The stub's `colorHex` defaults to white when the metadata color is
 * the BYLAYER / BYBLOCK sentinel — never rendered through the line
 * pipeline (only lifted records are), so the value is informational.
 *
 * Consumers that must tell a stub apart from a real renderable use the
 * predicate that ships with this format, in the sibling leaf module
 * `metadataStub.ts` (`isMetadataStubRenderable`) — the single truth shared by
 * `snap/osnapCandidates.ts` and `raycast/windowSelect.ts`. Any change to the
 * stub's `geometry.kind` / `type` contract here must be mirrored there.
 */
export function metadataToSpatialStub(
  meta: EntityMetadata,
  zOrder: number,
): RenderableEntity {
  const cx = (meta.bounds.minX + meta.bounds.maxX) * 0.5;
  const cy = (meta.bounds.minY + meta.bounds.maxY) * 0.5;
  return {
    id: meta.id,
    spaceId: meta.spaceId,
    type: meta.type as RenderableEntity["type"],
    bounds: { ...meta.bounds },
    layer: meta.layer,
    colorHex: resolveStubColor(meta.color),
    lineweightWorld: 0,
    // PR-LT3 — this is a degenerate POINT stub used only for the spatial
    // index / hit-test, never a line bucket, so its linetype never reaches a
    // dash material. We intentionally do NOT add a `linetype` byte to the
    // compact `EntityMetadata` store (RED ZONE: defeats the 685k-entity
    // bucketing for zero render benefit). Default to the ByLayer sentinel.
    linetype: "ByLayer",
    zOrder,
    geometry: {
      kind: "point",
      position: { x: cx, y: cy, z: 0 },
      pdmode: 0,
      pdsize: 0,
    },
  };
}

/**
 * Codex PR #784 round-8 P2 (`lod/lodPolicy.ts:217`) — METADATA-STUB FALLBACK for
 * the spatial index's entity resolver.
 *
 * After a cache-direct bulk load the index's resolver is
 * `renderableById.get(id)` over the LIFTED renderables only
 * (`SceneBulkLoad.ts finishSceneBulkLoad`). Every id the LOD pre-classify
 * dropped as sub-pixel (`partitionStubsByLod`) therefore resolved to
 * `undefined`, and `computeVisibleSet` (`lod/visibleSet.ts`) DROPS such ids —
 * so the per-frame re-lift (`FrameLoops` Pass 2) never saw them and a dropped
 * stub stayed dropped for the whole session. That single gap is why
 * `isAlwaysLiftedStubType` had to exempt whole entity kinds (and why exempting
 * `Block` made the bulk load materialise every INSERT's full
 * `childRenderables` tree — millions of children on a repeat-heavy drawing).
 *
 * This resolver closes the gap: an id the lifted map does not know falls back
 * to its METADATA STUB — the same shape `buildSpatialRecordsFromMetadata` fed
 * the index — so the id keeps flowing through the visible set, gets classified
 * by its real bbox, and reaches Pass 2's on-demand `liftRenderableForId`. The
 * stub is built on demand (never retained) so the fallback costs no memory on a
 * 685k-entity drawing; Pass 2 publishes the REAL renderable back into
 * `renderableById` the moment it lifts one, so the fallback is transient per id.
 *
 * Bounds come from the INDEX (`getBounds`), not from the metadata record: the
 * bulk-load pass reprojects annotative stubs in place before `bulkLoad`
 * (`reprojectAnnotativeStubBounds`), so the index — not the raw metadata — is
 * the authority for what the broad phase matched. An id absent from the index
 * resolves to `undefined` (it is not part of this pass's spatial set).
 *
 * DRAWORDER: a never-lifted stub carries `zOrder = 0`. The real per-entity
 * order is assigned by the bulk-load walk (`buildSpatialRecordsFromMetadata`)
 * and is not recoverable in O(1) from the compact metadata store; retaining a
 * per-id ordinal map would re-introduce exactly the per-entity retention the
 * cache-direct path exists to avoid. It only affects tie-breaking among
 * candidates that are still sub-pixel (i.e. never lifted); the moment Pass 2
 * lifts one, `renderableById` answers with the real renderable instead.
 */
export function createMetadataStubResolver(args: {
  metadataIndex: EntityMetadataIndex;
  getBounds: (id: string) => EntityMetadata["bounds"] | null;
  /**
   * LIVE camera zoom (CSS px per world unit) — the same number the per-frame
   * LOD classifier uses. Supplying it enables the sub-pixel FLOOR below: an id
   * this resolver would otherwise answer for, whose bbox measures smaller than
   * `HYSTERESIS_BANDS.skipLower` (0.45 px — strictly BELOW the hysteresis band
   * around the 0.5 px `skip` boundary), gets `undefined` instead of a freshly
   * allocated stub.
   *
   * Why: `computeVisibleSet` resolves EVERY rbush hit on every camera change.
   * At fit-to-extents on a 685k-entity drawing almost every hit is a
   * never-lifted id, so answering all of them would allocate hundreds of
   * thousands of throw-away stubs per pan/zoom step — trading the bulk-load
   * cost this change removed for a per-frame one.
   *
   * Why it is SAFE (not a re-introduction of the permanent drop): below
   * `skipLower` the hysteresis classifier returns the unguarded bucket, which
   * is `"skip"` for anything under 0.5 px — no previous bucket can hold such an
   * entity in `point` / `bbox-line` / `full`. So the ids this floor withholds
   * are exactly the ids the frame would classify `"skip"` and never draw. The
   * moment the camera makes one cross 0.45 px it resolves again and Pass 2
   * lifts it.
   *
   * Codex PR #784 round-9 P2 — the measurement is `boundsScreenSizePx`, the
   * SAME function the bucket classifier and the hysteresis layer use, NOT a
   * local `hypot × zoom`. That is what keeps "withheld" and "would be
   * classified skip" the same set at every zoom, and it is what stops the floor
   * from being permanent for a DEGENERATE (zero-diagonal) bbox: a local product
   * multiplies that zero by every future zoom and returns `undefined` forever,
   * so an INSERT whose definition is a lone POINT (or an empty / missing /
   * cyclic definition, which falls back to the bare insertion point) could never
   * be relifted even though the renderer paints a PDMODE marker for it. The
   * shared helper measures such a bbox as the renderer's own marker box
   * (`lod/lodPolicy.ts LOD_DEGENERATE_WORLD_DIAGONAL`), so it clears the floor
   * on zoom-in exactly like any other small entity.
   *
   * Codex PR #784 round-10 P2 — that measurement is RECOVERABILITY only: it
   * decides which ids this resolver still answers for, never that anything is
   * painted at them. Whether such an entity may be DRAWN as a points-cloud dot
   * is a separate, entity-shaped decision the bucket rule makes
   * (`lod/lodPolicy.ts entityDrawsDegenerateMarker` / `lodBucketForBounds`), so
   * an INSERT of an empty / missing definition stays resolvable (it is still
   * pickable at its insertion point) while drawing nothing.
   *
   * Omit it (or return a non-positive zoom) to answer for every known id.
   */
  cameraZoom?: () => number;
}): (id: string) => RenderableEntity | undefined {
  const { metadataIndex, getBounds, cameraZoom } = args;
  return (id: string): RenderableEntity | undefined => {
    // Bounds + floor FIRST: `getBounds` is an O(1) read of the rbush's own bbox
    // map, while `EntityMetadataIndex.get` re-materialises a record (interned
    // layer / colour strings). On the `computeVisibleSet` hot path most ids that
    // reach this resolver are below the floor, so the cheap test must come
    // first.
    const bounds = getBounds(id);
    if (!bounds || !isFiniteBounds(bounds)) return undefined;
    if (cameraZoom) {
      const zoom = cameraZoom();
      if (zoom > 0 && boundsScreenSizePx(bounds, zoom) < HYSTERESIS_BANDS.skipLower) {
        return undefined;
      }
    }
    const meta = metadataIndex.get(id);
    if (!meta) return undefined;
    return metadataToSpatialStub({ ...meta, bounds }, 0);
  };
}

/**
 * Lazily build the full `RenderableEntity` for one visible id.
 *
 * DEFUSE S11 — typed-first: the injected `resolveEntity` (backed by
 * `getEntityById`, see `SceneContentRefs.buildPreviewCallbacks`) resolves the
 * canonical `DrawingEntity`, and a typed-covered kind dispatches straight
 * through `entityToRenderableTyped` — the `buildShellEntityPreview` bag
 * flatten + `VisibleSetCache` entry are SKIPPED entirely for those kinds.
 * Only the bag-fallback kinds (`BAG_FALLBACK_RENDER_KINDS`) and
 * typed-resolution misses still take the legacy route:
 *   1. Building the `EntityPreview` (cached via `VisibleSetCache`).
 *   2. Running the legacy per-kind adapter (`entityToRenderable`).
 *
 * Lane H / S9 — Multileader now resolves HERE too (an explicit branch
 * inside `renderableFromDrawingEntity` itself, typed, no bag round-trip —
 * see `typedFirstRenderable.ts`'s `EXPLICIT_DISPATCH_RENDER_KINDS` doc).
 *
 * `Block` (INSERT) — fix "cache-direct never rendered INSERTs": one INSERT
 * flattens to MANY renderables, which never fit this one-id → one-renderable
 * contract, so the flatten used to run ONLY on the legacy branch
 * (`SceneBulkLoad.ts`) and every cache-direct / zoom re-lift of an INSERT
 * returned `null`. It now dispatches HERE, through the SAME typed flatten,
 * to a single `block-insert` WRAPPER renderable (`liftBlockInsertRenderable`,
 * `scene/liftBlockInsert.ts`) whose `childRenderables` are the flattened
 * children — consumed as render-only SATELLITES of the wrapper by the
 * bulk-load feed expansion and the per-frame cull / relift
 * (`scene/renderSatellites.ts`), exactly like MLEADER block content. The
 * S10d annotative gate keeps its order: `typedAnnotativeHidden` (the same
 * predicate `renderableFromDrawingEntity` applies to every other kind)
 * returns `null` for a hidden INSERT BEFORE the flatten runs.
 * `ctx.blocks` is the document block table `buildAdapterCtxBase` threads
 * (`SceneContent.tsx`'s `adapterCtxBase` memo) — the same table the legacy
 * branch passes explicitly.
 *
 * Returns `null` when the entity has been removed since the spatial
 * index was bulk-loaded, when the preview builder rejects the entity, when
 * it is annotatively hidden at the active scale, or when an INSERT flattens
 * to nothing (unknown block / empty block — "nothing to draw").
 */
export function liftRenderableForId(args: {
  id: string;
  cache: VisibleSetCache;
  buildPreview: (id: string) => EntityPreview | null;
  resolveEntity: TypedEntityResolver;
  ctx: AdapterContext;
}): RenderableEntity | null {
  const typed = args.resolveEntity(args.id);
  if (typed && typed.type === "Block") {
    // Gate order preserved: the same one-truth annotative predicate the
    // render gate / pick path use runs BEFORE the flatten (the flatten
    // re-checks it internally — `resolveBlockInsertTransform`).
    if (typedAnnotativeHidden(typed, args.ctx)) return null;
    return liftBlockInsertRenderable({
      typedInsert: typed,
      blocks: args.ctx.blocks ?? {},
      ctx: args.ctx,
    });
  }
  return renderableFromDrawingEntity({
    typed,
    buildPreview: () => args.cache.getOrBuild(args.id, args.buildPreview),
    ctx: args.ctx,
  });
}

/**
 * Build the full `RenderableEntity` array for a visible-id batch
 * (viewport-cull + LOD `full`/`bbox-line` buckets). Skips ids that
 * resolve to `null` (removed entities, bag-fallback kinds the legacy
 * adapter rejects, annotatively hidden entities, INSERTs with nothing to
 * draw). An INSERT lifts to its `block-insert` wrapper (see
 * {@link liftRenderableForId}).
 *
 * Each renderable's `zOrder` field is overridden with its index in the
 * input list so DRAWORDER follows the visible-set iteration order. The
 * caller is expected to feed ids in the order they came back from the
 * spatial index (which preserves the original metadata-index
 * insertion order).
 */
export function buildVisibleRenderables(args: {
  visibleIds: ReadonlyArray<string>;
  cache: VisibleSetCache;
  buildPreview: (id: string) => EntityPreview | null;
  resolveEntity: TypedEntityResolver;
  ctxBase: AdapterContext;
}): RenderableEntity[] {
  const out: RenderableEntity[] = [];
  for (let i = 0; i < args.visibleIds.length; i++) {
    const id = args.visibleIds[i];
    if (id === undefined) continue;
    const lifted = liftRenderableForId({
      id,
      cache: args.cache,
      buildPreview: args.buildPreview,
      resolveEntity: args.resolveEntity,
      ctx: { ...args.ctxBase, zOrderIndex: i },
    });
    if (lifted) out.push(lifted);
  }
  return out;
}

/**
 * Codex P1 #2 (PR #87) — async chunked variant of
 * {@link buildVisibleRenderables}. Lifts entities in batches of
 * `chunkSize` (default 2000) and yields to the event loop between
 * batches via `setTimeout(0)`. Lets the scene-bulk-load effect on the
 * streaming-import path (100k+ entities) drain the lift without
 * monopolising the main thread for ~5 seconds.
 *
 * Cancellation: the caller passes an `isAborted` predicate (typically
 * backed by a closure over a cleanup-mutated boolean flag). The
 * iterator checks it at every chunk boundary; on `true` it returns
 * `null` so the caller can skip the downstream mesh-build step.
 *
 * Returns `null` if cancelled mid-walk; otherwise the full lifted
 * `RenderableEntity[]` (same shape as the synchronous variant).
 */
export async function buildVisibleRenderablesChunked(args: {
  visibleIds: ReadonlyArray<string>;
  cache: VisibleSetCache;
  buildPreview: (id: string) => EntityPreview | null;
  resolveEntity: TypedEntityResolver;
  ctxBase: AdapterContext;
  chunkSize?: number;
  isAborted: () => boolean;
}): Promise<RenderableEntity[] | null> {
  const chunkSize = args.chunkSize ?? 2000;
  const out: RenderableEntity[] = [];
  const total = args.visibleIds.length;
  for (let i = 0; i < total; i += chunkSize) {
    if (args.isAborted()) return null;
    const end = Math.min(i + chunkSize, total);
    for (let j = i; j < end; j++) {
      const id = args.visibleIds[j];
      if (id === undefined) continue;
      const lifted = liftRenderableForId({
        id,
        cache: args.cache,
        buildPreview: args.buildPreview,
        resolveEntity: args.resolveEntity,
        ctx: { ...args.ctxBase, zOrderIndex: j },
      });
      if (lifted) out.push(lifted);
    }
    if (end < total) {
      // Yield to the event loop so Three.js / R3F can paint a frame
      // and any pending input handlers can fire before the next chunk.
      await new Promise<void>((resolve) => setTimeout(resolve, 0));
    }
  }
  if (args.isAborted()) return null;
  return out;
}

/**
 * S10d round-19/20 (@codex, textFromEntity:83 + spatialBuilder:290 — "Expand
 * annotative text stub bounds") — recompute annotative TEXT / MTEXT spatial STUB
 * bounds to their ACTIVE-scale DISPLAYED extent before the index is bulk-loaded.
 *
 * N2 (S10E-ANNOTATIVE-STUB-REPROJECT) extended this to annotative Block/INSERT
 * stubs — see the `stub.type === "Block"` branch below. Both share the same
 * root cause: the metadata index stores the entity's MASTER representation
 * (`state/entityMetadataIndex.ts boundsForEntity`) — a DEGENERATE point at
 * the master insertion for TEXT / MTEXT, and for a Block the definition's
 * extents under the MASTER transform (a degenerate point only when no block
 * table / no resolvable extents) — so an active-scale representation that
 * re-places the entity elsewhere leaves the spatial-index stub stale until
 * this pass runs.
 *
 * A cache-direct stub's bbox comes from the compact metadata index, whose
 * TEXT/MTEXT bounds are the entity's MASTER `insertionPoint` as a DEGENERATE
 * point (`state/entityMetadataIndex.ts boundsForEntity` → `fromPoints([insertionPoint])`).
 * But `textFromEntity` / `mtextFromEntity` RENDER an annotative entity at its
 * per-scale `anno.insertion` AND scale its glyphs to `height × effectiveScale`
 * model units (a 1:100 label is ~100× its paper height — hundreds of units
 * wide/high). Without this pass the spatial index (bulk-loaded BEFORE any lift,
 * and the source of pick / window-select CANDIDATES) holds the stub as a
 * zero-size point at the stale master insertion, so a click / window-select on
 * the DISPLAYED glyphs away from that point never returns the id.
 *
 * Recomputes each matched stub's bbox (and its degenerate point geometry) via the
 * SAME render-path adapter the lift uses — `textFromEntity` / `mtextFromEntity` →
 * `buildTextSceneGeometry` / `buildMTextSceneGeometry` for TEXT/MTEXT, or the
 * union of `flattenBlockInsertForRender`'s flattened children for Block/INSERT — so the
 * broad-phase box reflects the per-scale insertion AND the scaled/flattened
 * extent EXACTLY (single source of truth, no duplicated bbox math that could
 * drift from the render). HATCH is intentionally untouched (its boundary stays
 * in world coordinates — the resolver returns pattern-only overrides), and
 * ATTRIB stubs are placed / gated by their own flatten paths (they render as
 * part of their owning INSERT's flatten, not as a standalone cache-direct
 * stub). Non-annotative / no-active-scale / no-match entities resolve to no
 * active context and are left as-is (golden parity — an empty
 * `ctx.activeAnnotationScale` makes this a no-op).
 *
 * Mutates the stubs in place (they are freshly built by
 * {@link buildSpatialRecordsFromMetadata} and not yet handed to the index).
 */
export function reprojectAnnotativeStubBounds(args: {
  stubs: RenderableEntity[];
  resolveEntity: TypedEntityResolver;
  ctx: AdapterContext;
}): void {
  const { stubs, resolveEntity, ctx } = args;
  // No active scale → the resolver never matches a context; skip the whole pass
  // so a non-annotative document pays nothing. Round-24: normalize through the
  // shared `normalizeActiveScale` so a BLANK / whitespace-only header scale (which
  // `resolveTypedAnnotation` treats as no-active-scale) short-circuits here too,
  // instead of leaking past this truthiness guard into a wasted per-stub loop.
  if (!normalizeActiveScale(ctx.activeAnnotationScale)) return;
  for (const stub of stubs) {
    if (stub.type === "Text" || stub.type === "MText") {
      const typed = resolveEntity(stub.id);
      if (!typed || (typed.type !== "Text" && typed.type !== "MText")) continue;
      const anno = resolveTypedAnnotation(typed.annotativeContexts, ctx);
      // Only a matched per-scale context (`anno.activeContext`) reshapes the stub —
      // it carries BOTH the per-scale insertion and the `effectiveScale` that grows
      // the glyphs. Master fields (no match) → leave the metadata box untouched.
      if (!anno.activeContext) continue;
      // Recompute the DISPLAYED bounds through the render-path adapter (see JSDoc).
      const rendered =
        typed.type === "Text"
          ? textFromEntity(typed, ctx)
          : mtextFromEntity(typed, ctx);
      if (!isFiniteBounds(rendered.bounds)) continue;
      applyReprojectedBounds(stub, rendered.bounds);
      continue;
    }
    if (stub.type === "Block") {
      // N2 (S10E-ANNOTATIVE-STUB-REPROJECT, @codex round-19/20 gap) — mirror the
      // Text/MText branch above for annotative INSERTs. `filterAnnotativeHiddenStubs`
      // (the visibility half of this file) is already type-agnostic and handles
      // Block/INSERT correctly via `insertAnnotativeHidden` — this was the missing
      // POSITION-reprojection half. A matched per-scale `blockref` context
      // re-places/re-scales/re-rotates the WHOLE INSERT (`blockFromEntity`'s
      // top-level transform), so its flattened extent can land far from the
      // MASTER-transform stub (`entityMetadataIndex.ts boundsForEntity`'s
      // `case "Block"` — the definition extents under the master transform, a
      // degenerate point only when no block table / no resolvable extents) —
      // exactly the same class of staleness `textFromEntity`'s per-scale
      // insertion fixed for TEXT/MTEXT. No match (master fields render unchanged, or the OBJECTSCALE
      // allow-list overrides to visible-at-master) → leave the metadata box
      // untouched, same as the Text/MText "no context" case.
      const typed = resolveEntity(stub.id);
      if (!typed || typed.type !== "Block") continue;
      const anno = resolveTypedAnnotation(typed.annotativeContexts, ctx);
      if (!anno.activeContext) continue;
      // Flatten through the SAME one-truth seam the render/lift path uses
      // (`flattenBlockInsertForRender`, `scene/liftBlockInsert.ts` — the single
      // render call site of `blockFromEntity`; the legacy flat push and the
      // cache-direct wrapper call the identical `(typed, blocks, ctx)`
      // signature) — no second bbox-computation truth. `ctx.blocks` carries the
      // document's block-definition table (see `AdapterContext` doc); an absent
      // table (unit tests / not-yet-wired call sites) flattens to zero children
      // — the same golden-parity fallback the flatten's OWN callers rely on.
      const flattened = flattenBlockInsertForRender(typed, ctx.blocks ?? {}, ctx);
      let union: RenderableEntity["bounds"] | null = null;
      for (const child of flattened) {
        if (!isFiniteBounds(child.bounds)) continue;
        union = union ? unionBounds(union, child.bounds) : { ...child.bounds };
      }
      // No finite child bounds (block hidden at master / no renderable children)
      // → nothing to reproject onto; leave the metadata box.
      if (!union) continue;
      applyReprojectedBounds(stub, union);
      continue;
    }
  }
}

/**
 * Shared tail of {@link reprojectAnnotativeStubBounds}: overwrite the stub's
 * bounds + degenerate point geometry with the bbox a render-path adapter
 * (`textFromEntity` / `mtextFromEntity` / the union of
 * `flattenBlockInsertForRender`'s flattened children) computed for it.
 *
 * NO CONVERSION HAPPENS HERE, and that is the contract. The adapters' bounds
 * are OFFSET-world (their builders call `applyOffset` on absolute-position
 * inputs) and the stub array is OFFSET-world too
 * ({@link buildSpatialRecordsForSpaces} converts at the one seam), so the
 * value is already in the target frame and is written verbatim. The array
 * stays single-frame, which is the whole point: a mixed-frame array would
 * span the aggregate fit/LOD bbox and the spatial queries across the entire
 * origin distance.
 *
 * (Historically this called `unapplyOffset` to push the adapter's box BACK
 * into the raw metadata frame, because the rest of the array was raw then —
 * S10d round-21, @codex "Keep reprojected stubs in the metadata coordinate
 * frame". That inverse is now wrong: it would translate this one stub off by
 * the origin while every sibling stayed put, i.e. exactly the mixed-frame
 * array round-21 was written to prevent, just with the frames swapped.)
 */
function applyReprojectedBounds(
  stub: RenderableEntity,
  offsetWorldBounds: RenderableEntity["bounds"],
): void {
  stub.bounds = {
    minX: offsetWorldBounds.minX,
    minY: offsetWorldBounds.minY,
    maxX: offsetWorldBounds.maxX,
    maxY: offsetWorldBounds.maxY,
  };
  if (stub.geometry.kind === "point") {
    stub.geometry = {
      ...stub.geometry,
      position: {
        x: (offsetWorldBounds.minX + offsetWorldBounds.maxX) * 0.5,
        y: (offsetWorldBounds.minY + offsetWorldBounds.maxY) * 0.5,
        z: stub.geometry.position.z,
      },
    };
  }
}

/**
 * S10d round-21 (@codex, SceneBulkLoad:464 — "Drop hidden annotative stubs
 * before indexing") — remove cache-direct metadata stubs whose typed entity is
 * annotatively HIDDEN at the active scale (ANNOALLVISIBLE=false, no matching
 * representation AND not an OBJECTSCALE member) BEFORE the spatial index and the
 * fit/LOD pre-classify pass consume them.
 *
 * The cache-direct path bulk-loads the FULL stub array, then derives the
 * aggregate fit-to-extents bbox + per-stub LOD bucket from every stub's bounds
 * ({@link SceneBulkLoad}). A far-away HIDDEN annotation left in that array
 * distorts Zoom Extents and can make visible non-text geometry LOD-classify as
 * sub-pixel "skip". The later per-id lift DOES return `null` for these (via
 * `renderableFromDrawingEntity`'s gate → `typedAnnotativeHidden`), but by then the
 * stub already poisoned the rbush (it stays pickable) and the fit/LOD inputs. This
 * drops them up-front with the SAME predicate the render gate + pick path use
 * (`typedAnnotativeHidden` — one truth), so the three never diverge.
 *
 * Non-annotative / no-active-scale / ANNOALLVISIBLE=true → the predicate returns
 * `false` for every stub, so this returns the input array UNCHANGED (golden
 * parity — a default `AdapterContext` drops nothing and pays no allocation).
 */
export function filterAnnotativeHiddenStubs(args: {
  stubs: RenderableEntity[];
  resolveEntity: TypedEntityResolver;
  ctx: AdapterContext;
}): RenderableEntity[] {
  const { stubs, resolveEntity, ctx } = args;
  // No active scale OR ANNOALLVISIBLE=true → the render gate (`typedAnnotativeHidden`)
  // can hide NOTHING, so skip the whole O(N) per-stub `resolveEntity` pass AND the
  // filtered-array allocation:
  //   - a null active scale resolves every entity visible (see resolveTypedAnnotation);
  //   - ANNOALLVISIBLE=true forces both the per-scale resolver (no-match → FALLBACK_VISIBLE)
  //     and the OBJECTSCALE allow-list (`activeAllowListMembership` returns null when
  //     `annoAllVisible`) to a visible verdict for EVERY stub, so `typedAnnotativeHidden`
  //     is uniformly `false`.
  // In the normal default state (CANNOSCALE set + ANNOALLVISIBLE=true) hiding is
  // impossible, so a large cache-direct import must NOT pay a full-entity lookup pass
  // over hundreds of thousands of records before bulk-load. (@codex round-22,
  // spatialBuilder:367.) Round-24: normalize through the shared
  // `normalizeActiveScale` so a BLANK / whitespace-only active scale collapses to
  // "no active scale" here exactly as it does in `typedAnnotativeHidden` /
  // `activeAllowListMembership` — a blank scale hides nothing, so this returns the
  // stubs UNCHANGED instead of paying a full per-stub `resolveEntity` filter pass.
  if (
    !normalizeActiveScale(ctx.activeAnnotationScale) ||
    (ctx.annoAllVisible ?? true)
  )
    return stubs;
  return stubs.filter((stub) => {
    const typed = resolveEntity(stub.id);
    // Unresolvable stub (removed since bulk-load / bag-only kind) → keep it; we
    // cannot prove it is annotatively hidden, and the lift will drop it if so.
    if (!typed) return true;
    return !typedAnnotativeHidden(typed, ctx);
  });
}

function isFiniteBounds(b: EntityMetadata["bounds"]): boolean {
  return (
    Number.isFinite(b.minX) &&
    Number.isFinite(b.minY) &&
    Number.isFinite(b.maxX) &&
    Number.isFinite(b.maxY)
  );
}

function resolveStubColor(color: string | null): string {
  if (color === null) return "#ffffff";
  // `BYLAYER` / `BYBLOCK` sentinels resolve to layer/block lookups
  // when the entity is lifted — for the stub we just need a valid
  // hex string. White is benign because the stub never reaches a
  // mesh.
  if (color === "BYLAYER" || color === "BYBLOCK") return "#ffffff";
  return color;
}
